/**
 * Import Monitor — repair one PO line against the ERP balance, by hand.
 *
 * A compact GRN Repair board for the line (same drag / move / split / delete /
 * schedule edits as the GRN Allocation Repair page), with each slot showing
 * Helix's balance beside the ERP's so the operator can move receipts until the
 * slots agree. Closest-match rebalance is available as on the repair page.
 */
import { useEffect, useMemo } from 'react';
import { useGrnRepairLine } from './useGrnRepairLine';
import GrnRepairBoard, { ClosestMatchButton } from './GrnRepairBoard';
import { Button } from '../../components/ui/button';
import { AlertTriangle, CheckCircle2, Loader2, X } from 'lucide-react';

interface ErpMismatchRepairModalProps {
  /** PO line to repair; null keeps the modal closed. */
  poLineId: number | null;
  /** "PO/line" shown in the title. */
  label: string;
  onClose: () => void;
  /** Called after any edit lands, so the mismatch list can refresh. */
  onChanged: () => void;
}

export default function ErpMismatchRepairModal({ poLineId, label, onClose, onChanged }: ErpMismatchRepairModalProps) {
  const line = useGrnRepairLine(onChanged);
  const { detail, detailLoading, detailError, fetchDetail } = line;

  useEffect(() => {
    if (poLineId != null) fetchDetail(poLineId);
  }, [poLineId, fetchDetail]);

  useEffect(() => {
    if (poLineId == null) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [poLineId, onClose]);

  const mismatched = useMemo(() => (detail?.schedules ?? []).filter((s) =>
    s.erp_balance_qty != null && s.status !== 'cancelled' && !s.is_synthetic
    && parseFloat(s.balance_qty) !== parseFloat(s.erp_balance_qty),
  ), [detail]);
  // Cancelled in Helix while the ERP still reports quantity open (shortage report rows).
  const cancelledButOpen = useMemo(() => (detail?.schedules ?? []).filter((s) =>
    s.status === 'cancelled' && !s.is_synthetic && parseFloat(s.erp_balance_qty ?? '0') > 0,
  ), [detail]);

  if (poLineId == null) return null;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-40" onClick={onClose}>
      <div
        className="bg-background rounded-lg shadow-xl w-[min(1100px,95vw)] max-h-[90vh] flex flex-col"
        role="dialog"
        aria-label={`Repair ${label} against ERP`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b">
          <div>
            <div className="text-lg font-semibold">{label} — match receipts to the ERP</div>
            <div className="text-xs text-muted-foreground">
              {detail ? `${detail.po_line.supplier_name ?? ''} • part ${detail.po_line.part_number} • OTIF window: ${detail.otif_window.days_early}d early / ${detail.otif_window.days_late}d late` : 'Loading…'}
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close"><X className="h-4 w-4" /></Button>
        </div>

        <div className="px-5 py-4 overflow-auto flex-1">
          {detailLoading && !detail && (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading PO line…
            </div>
          )}
          {detailError && <div className="text-sm text-red-600 mb-3">{detailError}</div>}
          {detail && (
            <GrnRepairBoard
              line={line}
              showErp
              header={(
                <div className="space-y-3 mb-3">
                  {detail.awaiting_erp && (
                    <div className="flex gap-2 items-start rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                      <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                      <span>A receipt reached Helix after the ERP last reported this line, so the ERP balance may not include it yet. Check before moving receipts.</span>
                    </div>
                  )}
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    {mismatched.length === 0 && cancelledButOpen.length === 0 ? (
                      <div className="flex items-center gap-2 text-sm text-green-700">
                        <CheckCircle2 className="h-4 w-4" /> Every slot the ERP reports matches Helix.
                      </div>
                    ) : (
                      <div className="text-sm space-y-0.5">
                        {mismatched.length > 0 && (
                          <div>
                            <span className="font-semibold text-red-700">{mismatched.length} slot{mismatched.length === 1 ? '' : 's'}</span> differ from the ERP.
                            Move or split receipts until each slot shows ✓.
                          </div>
                        )}
                        {cancelledButOpen.length > 0 && (
                          <div>
                            <span className="font-semibold text-red-700">{cancelledButOpen.length} slot{cancelledButOpen.length === 1 ? '' : 's'}</span> cancelled in Helix
                            {' '}but still open in the ERP. Un-cancel if the ERP is right.
                          </div>
                        )}
                      </div>
                    )}
                    <ClosestMatchButton line={line} />
                  </div>
                </div>
              )}
            />
          )}
        </div>

        <div className="flex justify-end px-5 py-3 border-t">
          <Button onClick={onClose}>Done</Button>
        </div>
      </div>
    </div>
  );
}
